<template>
    <div class="panel">
        <div class="w-full flex items-center justify-between mb-8">
            <h2 class="uppercase text-blue-light font-thin text-lg">
                {{ $t('permitting_type_and_extra_fields') }}
            </h2>
        </div>

        <b-table :data="permissionTypes" :loading="isLoading" detailed detail-key="id" :show-detail-icon="true"
            paginated :per-page="5" striped bordered icon-pack="fas" @details-open="fetchExtraFields">
            <b-table-column field="name" :label="$t('name')" v-slot="props">
                {{ props.row.name }}
            </b-table-column>
            <b-table-column field="code" :label="$t('code')" v-slot="props">
                {{ props.row.code }}
            </b-table-column>
            <b-table-column :label="$t('actions')" width="180" centered v-slot="props">
                <div class="flex items-center justify-center space-x-2">
                    <b-tooltip :label="$t('assign_extrafields')" position="is-left">
                        <div @click.stop="openAddExtraFieldModal(props.row)"
                            class="cursor-pointer flex text-green ml-2">
                            <b-icon pack="fas" icon="plus-circle" />
                        </div>
                    </b-tooltip>
                </div>
            </b-table-column>
            <template #detail="props">
                <div class="ml-6 mb-4">
                    <h3 class="font-bold mb-2">{{ $t('extrafields') }}</h3>
                    <b-table v-if="extraFields[props.row.id] && extraFields[props.row.id].length"
                        :data="extraFields[props.row.id]" :loading="false" striped bordered icon-pack="fas">
                        <b-table-column field="name" :label="$t('name')" v-slot="fieldProps">
                            {{ fieldProps.row.name }}
                        </b-table-column>
                        <b-table-column field="type" :label="$t('type')" v-slot="fieldProps">
                            {{ fieldProps.row.type }}
                        </b-table-column>
                        <b-table-column field="order_column" :label="$t('order')" v-slot="fieldProps">
                            {{ fieldProps.row.order_column }}
                        </b-table-column>
                        <b-table-column :label="$t('actions')" v-slot="fieldProps">
                            <b-tooltip :label="$t('edit')" position="is-left">
                                <div @click.stop="openEditExtraFieldModal(props.row, fieldProps.row)"
                                    class="cursor-pointer flex text-blue ml-2">
                                    <b-icon pack="fas" icon="pencil-alt" />
                                </div>
                            </b-tooltip>
                            <b-tooltip :label="$t('remove')" position="is-left" type="is-danger">
                                <div @click.stop="deleteExtraField(fieldProps.row, props.row)"
                                    class="cursor-pointer flex text-red ml-2">
                                    <b-icon pack="fas" icon="trash-alt" />
                                </div>
                            </b-tooltip>
                        </b-table-column>
                    </b-table>
                    <div v-else class="has-text-centered py-4">{{ $t('no_results') }}</div>
                </div>
            </template>
            <template #empty>
                <div class="has-text-centered py-4">{{ $t('no_results') }}</div>
            </template>
        </b-table>

        <!-- Modal para agregar/editar extra field -->
        <b-modal :active.sync="isAddExtraFieldModalActive" :width="400" has-modal-card>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        {{ isEditing ? $t('edit_extra_field') : $t('add_extrafield') }}
                    </p>
                    <button class="delete" aria-label="close" @click="closeExtraFieldModal"></button>
                </header>
                <section class="modal-card-body">
                    <b-field :label="$t('name')">
                        <b-input v-model="newExtraField.name" :placeholder="$t('name')" />
                    </b-field>
                    <b-field :label="$t('type')">
                        <b-select v-model="newExtraField.type" expanded>
                            <option value="text">Text</option>
                            <!-- <option value="date">Date</option> -->
                            <!-- <option value="number">Number</option> -->
                        </b-select>
                    </b-field>
                    <b-field :label="$t('order')">
                        <b-input
                            v-model.number="newExtraField.order_column"
                            type="number"
                            min="0"
                            :placeholder="$t('order')"
                        />
                    </b-field>
                </section>
                <footer class="modal-card-foot">
                    <b-button type="is-primary" @click="isEditing ? updateExtraField() : addExtraField()"
                        :disabled="!canAddField">
                        {{ isEditing ? $t('save') : $t('add') }}
                    </b-button>
                    <b-button @click="closeExtraFieldModal">{{ $t('cancel') }}</b-button>
                </footer>
            </div>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    props: {
        company: { type: Object, required: true }
    },
    data() {
        return {
            permissionTypes: [],
            isLoading: false,
            extraFields: {},
            isAddExtraFieldModalActive: false,
            currentPermittingType: null,
            newExtraField: { name: '', type: 'text' },
            isEditing: false,
            editingFieldId: null,
        }
    },
    mounted() {
        this.fetchPermissionTypes();
        this.fetchAllExtraFields();
    },
    computed: {
        canAddField() {
            return this.newExtraField.name && this.newExtraField.type
        }
    },
    methods: {
        async fetchPermissionTypes(page = 1) {
            this.isLoading = true
            try {
                const { data } = await axios.get(`/api/v2/company/${this.company.id}/permitting-types`, {
                    params: { page, perPage: 5 }
                })
                this.permissionTypes = data.types || []
            } finally {
                this.isLoading = false
            }
        },
        async fetchExtraFields(type) {
            const id = type.id
            try {
                const { data } = await axios.get(`/api/v2/permitting-types/${id}/extra-fields`)
                this.$set(this.extraFields, id, data.fields || [])
            } catch {
                this.$set(this.extraFields, id, [])
            }
        },
        openAddExtraFieldModal(type) {
            this.currentPermittingType = type
            this.newExtraField = { name: '', type: 'text', order_column: 0 }
            this.isEditing = false
            this.editingFieldId = null
            this.isAddExtraFieldModalActive = true
        },
        openEditExtraFieldModal(permittingType, field) {
            this.currentPermittingType = permittingType
            this.newExtraField = { name: field.name, type: field.type, order_column: field.order_column }
            this.isEditing = true
            this.editingFieldId = field.id
            this.isAddExtraFieldModalActive = true
        },
        closeExtraFieldModal() {
            this.isAddExtraFieldModalActive = false
            this.newExtraField = { name: '', type: 'text' }
            this.isEditing = false
            this.editingFieldId = null
        },
        async addExtraField() {
            if (!this.canAddField || !this.currentPermittingType) return
            try {
                await axios.post(`/api/v2/permitting-extra-fields`, {
                    permitting_type_id: this.currentPermittingType.id,
                    name: this.newExtraField.name,
                    type: this.newExtraField.type,
                    order_column: this.newExtraField.order_column,
                })
                this.$buefy.toast.open({ message: this.$t('save_successful'), type: 'is-success' })
                this.closeExtraFieldModal()
                await this.fetchExtraFields(this.currentPermittingType)
            } catch {
                this.$buefy.toast.open({ message: this.$t('save_error'), type: 'is-danger' })
            }
        },
        async updateExtraField() {
            if (!this.canAddField || !this.currentPermittingType || !this.editingFieldId) return
            try {
                await axios.put(`/api/v2/permitting-extra-fields/${this.editingFieldId}`, {
                    name: this.newExtraField.name,
                    type: this.newExtraField.type,
                    order_column: this.newExtraField.order_column,
                })
                this.$buefy.toast.open({ message: this.$t('save_successful'), type: 'is-success' })
                this.closeExtraFieldModal()
                await this.fetchExtraFields(this.currentPermittingType)
            } catch {
                this.$buefy.toast.open({ message: this.$t('save_error'), type: 'is-danger' })
            }
        },
        async deleteExtraField(field, permittingType) {
            this.$dialog.confirm({
                title: this.$t('remove'),
                message: this.$t('delete_confirm_text', [field.name]),
                confirmText: this.$t('remove'),
                type: 'is-danger',
                hasIcon: true,
                onConfirm: async () => {
                    try {
                        await axios.delete(`/api/v2/permitting-extra-fields/${field.id}`)
                        this.$buefy.toast.open({
                            message: this.$t('removed_successfully'),
                            type: 'is-success'
                        })
                        await this.fetchExtraFields(permittingType)
                    } catch (error) {
                        this.$buefy.toast.open({
                            message: this.$t('remove_error') + (error.response?.data?.message || ''),
                            type: 'is-danger'
                        })
                    }
                }
            })
        },
    },
    mounted() {
        this.fetchPermissionTypes()
    }
}
</script>